import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { API_URL } from "../api";
import { projects, suggestedQuestions, t, ui } from "../data/portfolio";

/* Arama sonuçlarında gezilebilecek hedefler */
function buildTargets(lang) {
  const labels = ui[lang];
  const projectRows = projects.map((p) => ({
    id: `p-${p.slug}`,
    label: t(p.name, lang),
    hint: t(p.kind, lang),
    href: `#/proje/${p.slug}`,
  }));
  const sectionRows = [
    { id: "s-work", label: labels.sections.work, href: "#work" },
    { id: "s-ledger", label: labels.sections.ledger, href: "#ledger" },
    { id: "s-experience", label: labels.sections.experience, href: "#experience" },
    { id: "s-toolkit", label: labels.sections.toolkit, href: "#toolkit" },
    { id: "s-about", label: labels.sections.about, href: "#about" },
    { id: "s-contact", label: labels.sections.contact, href: "#contact" },
  ];
  return [...projectRows, ...sectionRows];
}

const normalize = (s) =>
  s
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");

export default function CommandPalette({ open, onClose, lang, initialQuestion }) {
  const labels = ui[lang].palette;
  const [mode, setMode] = useState("search"); // "search" | "chat"
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);

  const inputRef = useRef(null);
  const chatInputRef = useRef(null);
  const scrollRef = useRef(null);
  const restoreFocus = useRef(null);

  const targets = useMemo(() => buildTargets(lang), [lang]);

  const results = useMemo(() => {
    if (!query.trim()) return targets.slice(0, 6);
    const q = normalize(query.trim());
    return targets
      .filter((item) => normalize(item.label + " " + (item.hint || "")).includes(q))
      .slice(0, 7);
  }, [query, targets]);

  const send = useCallback(
    async (text) => {
      const question = text.trim();
      if (!question || pending) return;

      setMode("chat");
      setQuery("");
      const next = [...messages, { sender: "user", text: question }];
      setMessages(next);
      setPending(true);

      try {
        const { data } = await axios.post(`${API_URL}/sor`, {
          messages: next.map((m) => ({ role: m.sender, content: m.text })),
          dil: lang,
        });
        setMessages((prev) => [...prev, { sender: "bot", text: data.cevap }]);
      } catch (err) {
        setMessages((prev) => [...prev, { sender: "bot", text: labels.error, failed: true }]);
      } finally {
        setPending(false);
      }
    },
    [messages, pending, lang, labels.error]
  );

  /* Açılış / kapanış */
  useEffect(() => {
    if (open) {
      restoreFocus.current = document.activeElement;
      document.body.style.overflow = "hidden";
      if (initialQuestion) send(initialQuestion);
    } else {
      document.body.style.overflow = "";
      setQuery("");
      setCursor(0);
      if (restoreFocus.current && restoreFocus.current.focus) restoreFocus.current.focus();
    }
    return () => {
      document.body.style.overflow = "";
    };
    // initialQuestion yalnızca açılış anında okunur
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = mode === "chat" ? chatInputRef.current : inputRef.current;
    if (el) el.focus();
  }, [open, mode]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  useEffect(() => setCursor(0), [query]);

  const goTo = useCallback(
    (href) => {
      onClose();
      const already = window.location.hash === href;
      window.location.hash = href;
      if (already && !href.startsWith("#/")) {
        const el = document.getElementById(href.slice(1));
        if (el) {
          window.requestAnimationFrame(() =>
            el.scrollIntoView({ behavior: "smooth", block: "start" })
          );
        }
      }
    },
    [onClose]
  );

  if (!open) return null;

  const rows = query.trim()
    ? [...results, { id: "__ask", ask: true, label: query.trim() }]
    : results;

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c - 1 + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[cursor];
      if (!row) return;
      if (row.ask) send(row.label);
      else goTo(row.href);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <div className="pal-scrim" onMouseDown={onClose}>
      <div
        className="pal"
        role="dialog"
        aria-modal="true"
        aria-label={mode === "chat" ? labels.ask : labels.placeholder}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {mode === "search" ? (
          <>
            <div className="pal-field">
              <span className="pal-caret" aria-hidden="true" />
              <input
                ref={inputRef}
                className="pal-input"
                value={query}
                placeholder={labels.placeholder}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                aria-label={labels.placeholder}
              />
              <kbd className="pal-esc" onClick={onClose}>
                esc
              </kbd>
            </div>

            <div className="pal-body">
              <ul className="pal-list" role="listbox">
                {rows.map((row, i) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className={`pal-row ${i === cursor ? "is-active" : ""}`}
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => (row.ask ? send(row.label) : goTo(row.href))}
                    >
                      <span className="pal-kind">{row.ask ? labels.askThis : labels.goto}</span>
                      <span className="pal-label">{row.label}</span>
                      {row.hint && <span className="pal-hint">{row.hint}</span>}
                    </button>
                  </li>
                ))}
              </ul>

              {!query.trim() && (
                <div className="pal-suggested">
                  <p className="pal-suggested-head">{labels.suggestions}</p>
                  {suggestedQuestions[lang].map((q) => (
                    <button key={q} type="button" className="pal-chip" onClick={() => send(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="pal-chat-head">
              <button type="button" className="pal-back" onClick={() => setMode("search")}>
                {labels.backToSearch}
              </button>
              <kbd className="pal-esc" onClick={onClose}>
                esc
              </kbd>
            </div>

            <div className="pal-thread" ref={scrollRef}>
              {messages.map((m, i) => (
                <div key={i} className={`pal-msg pal-msg--${m.sender} ${m.failed ? "is-failed" : ""}`}>
                  {m.sender === "bot" ? <ReactMarkdown>{m.text}</ReactMarkdown> : <p>{m.text}</p>}
                </div>
              ))}
              {pending && (
                <div className="pal-msg pal-msg--bot pal-typing">
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>

            <form
              className="pal-field pal-field--chat"
              onSubmit={(e) => {
                e.preventDefault();
                send(query);
              }}
            >
              <span className="pal-caret" aria-hidden="true" />
              <input
                ref={chatInputRef}
                className="pal-input"
                value={query}
                placeholder={labels.ask}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && onClose()}
                aria-label={labels.ask}
              />
              <button type="submit" className="pal-send" disabled={pending || !query.trim()}>
                ↵
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
